import styles from './styles.module.scss';
import { DailySunlightResult } from '@/types/sunlight';

interface FacadeWallDataProps {
	data: DailySunlightResult[];
}

const average = (values: Array<number | undefined | null>) => {
	const valid = values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
	if (!valid.length) return 0;
	return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const maxValue = (values: Array<number | undefined | null>) => {
	const valid = values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
	if (!valid.length) return 0;
	return Math.max(...valid);
};

const minValue = (values: Array<number | undefined | null>) => {
	const valid = values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
	if (!valid.length) return 0;
	return Math.min(...valid);
};

const sum = (values: Array<number | undefined | null>) => {
	const valid = values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
	return valid.reduce((acc, value) => acc + value, 0);
};

const formatDate = (date: string | null | undefined) => {
	if (!date) return 'N/A';
	const parsed = new Date(date);
	if (Number.isNaN(parsed.getTime())) return 'N/A';
	return parsed.toLocaleDateString();
};

const minDate = (values: Array<string | null | undefined>) => {
	const validTimes = values
		.map((value) => (value ? new Date(value).getTime() : NaN))
		.filter((time) => Number.isFinite(time));
	if (!validTimes.length) return null;
	return new Date(Math.min(...validTimes)).toISOString();
};

const maxDate = (values: Array<string | null | undefined>) => {
	const validTimes = values
		.map((value) => (value ? new Date(value).getTime() : NaN))
		.filter((time) => Number.isFinite(time));
	if (!validTimes.length) return null;
	return new Date(Math.max(...validTimes)).toISOString();
};

export const FacadeWallData = ({ data }: FacadeWallDataProps) => {
	if (data.length === 0) {
		return null;
	}

	// Calculate aggregated statistics
	const avgHours = average(data.map((d) => d.averageDailySunlightHours));
	const maxHours = maxValue(data.map((d) => d.averageDailySunlightHours));
	const minHours = minValue(data.map((d) => d.averageDailySunlightHours));
	const avgMorning = average(data.map((d) => d.morningHoursPercent));
	const avgAfternoon = average(data.map((d) => d.afternoonHoursPercent));
	const avgSpring = average(data.map((d) => d.springMonthsAverage));
	const avgSummer = average(data.map((d) => d.summerMonthsAverage));
	const avgFall = average(data.map((d) => d.fallMonthsAverage));
	const avgWinter = average(data.map((d) => d.winterMonthsAverage));
	const firstLightDate = minDate(data.map((d) => d.firstSunlightDate));
	const lastLightDate = maxDate(data.map((d) => d.lastSunlightDate));

	const hoursColor = avgHours > 6 ? '#00FF00' : avgHours > 4 ? '#FFD700' : '#FF6B6B';

	const wallCount = new Set(
		data.map((d: DailySunlightResult & { wallId?: string }) => d.wallId)
	).size;

	return (
		<div>
			<div className={styles.header}>
				<span className={styles.title}>
					Facade Wall
				</span>
			</div>

			<div className={styles.primary}>
				<div className={styles.mainMetric} style={{ color: hoursColor }}>
					{avgHours.toFixed(1)} hrs/day
				</div>
				<div className={styles.consistency}>
					<span className={styles.label}>Average Sunlight</span>
				</div>
			</div>

			<div className={styles.stats}>
				<div className={styles.statRow}>
					<span className={styles.statLabel}>Maximum:</span>
					<span className={styles.statValue}>{maxHours.toFixed(1)} hrs</span>
				</div>
				<div className={styles.statRow}>
					<span className={styles.statLabel}>Minimum:</span>
					<span className={styles.statValue}>{minHours.toFixed(1)} hrs</span>
				</div>
				<div className={styles.statRow}>
					<span className={styles.statLabel}>Range:</span>
					<span className={styles.statValue}>{(maxHours - minHours).toFixed(1)} hrs</span>
				</div>
				<div className={styles.statRow}>
					<span className={styles.statLabel}>Morning / Afternoon:</span>
					<span className={styles.statValue}>{avgMorning.toFixed(0)}% / {avgAfternoon.toFixed(0)}%</span>
				</div>
				<div className={styles.statRow}>
					<span className={styles.statLabel}>Spring / Summer:</span>
					<span className={styles.statValue}>{avgSpring.toFixed(1)}h / {avgSummer.toFixed(1)}h</span>
				</div>
				<div className={styles.statRow}>
					<span className={styles.statLabel}>Fall / Winter:</span>
					<span className={styles.statValue}>{avgFall.toFixed(1)}h / {avgWinter.toFixed(1)}h</span>
				</div>
			</div>

			<div className={styles.meta}>
				<div className={styles.metaRow}>
					<span className={styles.metaLabel}>First light:</span>
					<span className={styles.metaValue}>{formatDate(firstLightDate)}</span>
				</div>
				<div className={styles.metaRow}>
					<span className={styles.metaLabel}>Last light:</span>
					<span className={styles.metaValue}>{formatDate(lastLightDate)}</span>
				</div>
				<div className={styles.metaNote}>
					Aggregated from {wallCount} wall segment{wallCount !== 1 ? 's' : ''} ({data.length} grid points) on same axis
				</div>
			</div>
		</div>
	);
};
